import React from 'react';
import moment from 'moment';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Divider,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import SaveIcon from '@mui/icons-material/Save';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import RefreshIcon from '@mui/icons-material/Refresh';
import EditIcon from '@mui/icons-material/Edit';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import PlaylistAddIcon from '@mui/icons-material/PlaylistAdd';

import {
  createRequisition,
  fetchBudgetCycles,
  fetchBudgetPeriods,
  fetchCostCenters,
  fetchPriorities,
  fetchPrompts,
  fetchRequisitionTypes,
  fetchResourceTypes,
  fetchUoms,
  generateAiDraft,
  IAiDraftResponse,
  refineAiDraft,
} from './forms.apis';
import RequisitionItemDialog from './RequisitionItemDialog';
import {
  IAiExtractForm,
  ILookupItem,
  IRequisitionHeaderForm,
  IRequisitionItemForm,
  IRequisitionSavePayload,
} from './forms.models';

type Props = {
  navigate?: (path: string) => void;
};

type State = {
  loading: boolean;
  generating: boolean;
  refining: boolean;
  saving: boolean;
  error: string;
  success: string;

  rawInput: string;
  refineInstruction: string;

  requisition: IRequisitionHeaderForm;
  items: IRequisitionItemForm[];
  assumptions: string[];
  clarifications: string[];
  aiextract: IAiExtractForm | null;

  requisitionTypes: ILookupItem[];
  periods: ILookupItem[];
  cycles: ILookupItem[];
  costcenters: ILookupItem[];
  resourceTypes: ILookupItem[];
  uoms: ILookupItem[];
  priorities: ILookupItem[];
  prompts: ILookupItem[];

  selectedPromptix: string;

  itemDialogOpen: boolean;
  itemDialogMode: 'add' | 'edit';
  editingItemIndex: number;
  editingItem: IRequisitionItemForm;
};

export default class RequisitionAIDraftPage extends React.Component<any, State> {
  constructor(props: Props) {
    super(props);

    this.state = {
      loading: false,
      generating: false,
      refining: false,
      saving: false,
      error: '',
      success: '',

      rawInput: '',
      refineInstruction: '',

      requisition: this.getEmptyHeader(),
      items: [],
      assumptions: [],
      clarifications: [],
      aiextract: null,

      requisitionTypes: [],
      periods: [],
      cycles: [],
      costcenters: [],
      resourceTypes: [],
      uoms: [],
      priorities: [],
      prompts: [],

      selectedPromptix: '',

      itemDialogOpen: false,
      itemDialogMode: 'add',
      editingItemIndex: -1,
      editingItem: this.getEmptyItem(1),
    };
  }

  componentDidMount(): void {
    this.loadLookups();
  }

  private getEmptyHeader = (): IRequisitionHeaderForm => {
    return {
      recname: '',
      shortcode: '',
      description: '',
      typeix: '',
      periodix: '',
      cycleix: '',
      costcenterix: '',
      reference: '',
      purpose: '',
      requesteddate: moment().format('YYYY-MM-DD'),
      neededfrom: '',
      neededto: '',
      aiassisted: 1,
      rawinput: '',
    };
  };

  private getEmptyItem = (lineno: number): IRequisitionItemForm => {
    return {
      lineno,
      recname: '',
      shortcode: '',
      description: '',
      typeix: '',
      quantity: '',
      uomix: '',
      unitestimate: '',
      totalestimate: '',
      neededfrom: '',
      neededto: '',
      justification: '',
      expectedoutcome: '',
      technicalspec: '',
      priorityix: '',
      kpinote: '',
    };
  };

  private loadLookups = async (): Promise<void> => {
    this.setState({
      loading: true,
      error: '',
    });

    try {
      const [
        requisitionTypes,
        periods,
        cycles,
        costcenters,
        resourceTypes,
        uoms,
        priorities,
        prompts,
      ] = await Promise.all([
        fetchRequisitionTypes(),
        fetchBudgetPeriods(),
        fetchBudgetCycles(),
        fetchCostCenters(),
        fetchResourceTypes(),
        fetchUoms(),
        fetchPriorities(),
        fetchPrompts(),
      ]);

      this.setState({
        loading: false,
        requisitionTypes,
        periods,
        cycles,
        costcenters,
        resourceTypes,
        uoms,
        priorities,
        prompts,
        selectedPromptix: prompts.length > 0 ? prompts[0].recix : '',
      });
    } catch (error: any) {
      this.setState({
        loading: false,
        error: error?.message || 'Failed to load AI draft page',
      });
    }
  };

  private handleRawInputChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ): void => {
    this.setState({
      rawInput: event.target.value,
    });
  };

  private handleRefineInstructionChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ): void => {
    this.setState({
      refineInstruction: event.target.value,
    });
  };

  private handleHeaderTextChange =
    (field: keyof IRequisitionHeaderForm) =>
    (event: React.ChangeEvent<HTMLInputElement>): void => {
      const value = event.target.value;

      this.setState((prevState) => ({
        requisition: {
          ...prevState.requisition,
          [field]: value,
        },
      }));
    };

  private handleHeaderSelectChange =
    (field: keyof IRequisitionHeaderForm) =>
    (event: SelectChangeEvent<string>): void => {
      const value = event.target.value;

      this.setState((prevState) => ({
        requisition: {
          ...prevState.requisition,
          [field]: value,
        },
      }));
    };

  private handlePromptChange = (event: SelectChangeEvent<string>): void => {
    this.setState({
      selectedPromptix: event.target.value,
    });
  };

  private mapLookupByCodeOrIx = (
    lookupItems: ILookupItem[],
    maybeIx?: string,
    maybeCode?: string | null
  ): string => {
    if (maybeIx) return maybeIx;

    if (maybeCode) {
      const found = lookupItems.find(
        (x) =>
          (x.shortcode || '').toUpperCase() === maybeCode.toUpperCase()
      );
      if (found) return found.recix;
    }

    return '';
  };

  private mapAiDraftToForm = (draftResponse: IAiDraftResponse): void => {
    const {
      requisitionTypes,
      periods,
      cycles,
      costcenters,
      resourceTypes,
      uoms,
      priorities,
      selectedPromptix,
      rawInput,
    } = this.state;

    const draft = draftResponse.draft;

    const requisition: IRequisitionHeaderForm = {
      recname: draft.requisition.recname || '',
      shortcode: draft.requisition.shortcode || '',
      description: draft.requisition.description || '',
      typeix: this.mapLookupByCodeOrIx(
        requisitionTypes,
        draft.requisition.typeix,
        draft.requisition.typecode
      ),
      periodix: this.mapLookupByCodeOrIx(
        periods,
        draft.requisition.periodix,
        draft.requisition.periodcode
      ),
      cycleix: this.mapLookupByCodeOrIx(
        cycles,
        draft.requisition.cycleix,
        draft.requisition.cyclecode
      ),
      costcenterix: this.mapLookupByCodeOrIx(
        costcenters,
        draft.requisition.costcenterix,
        draft.requisition.costcentercode
      ),
      reference: draft.requisition.reference || '',
      purpose: draft.requisition.purpose || '',
      requesteddate: draft.requisition.requesteddate
        ? moment(draft.requisition.requesteddate).format('YYYY-MM-DD')
        : moment().format('YYYY-MM-DD'),
      neededfrom: draft.requisition.neededfrom
        ? moment(draft.requisition.neededfrom).format('YYYY-MM-DD')
        : '',
      neededto: draft.requisition.neededto
        ? moment(draft.requisition.neededto).format('YYYY-MM-DD')
        : '',
      aiassisted: 1,
      rawinput: draft.requisition.rawinput || rawInput,
    };

    const items: IRequisitionItemForm[] = (draft.items || []).map((item, index) => ({
      lineno: item.lineno || index + 1,
      recname: item.recname || '',
      shortcode: item.shortcode || '',
      description: item.description || '',
      typeix: this.mapLookupByCodeOrIx(
        resourceTypes,
        item.typeix,
        item.typecode
      ),
      quantity:
        item.quantity === null || item.quantity === undefined ? '' : item.quantity,
      uomix: this.mapLookupByCodeOrIx(
        uoms,
        item.uomix,
        item.uomcode
      ),
      unitestimate:
        item.unitestimate === null || item.unitestimate === undefined
          ? ''
          : item.unitestimate,
      totalestimate:
        item.totalestimate === null || item.totalestimate === undefined
          ? ''
          : item.totalestimate,
      neededfrom: item.neededfrom
        ? moment(item.neededfrom).format('YYYY-MM-DD')
        : '',
      neededto: item.neededto ? moment(item.neededto).format('YYYY-MM-DD') : '',
      justification: item.justification || '',
      expectedoutcome: item.expectedoutcome || '',
      technicalspec: item.technicalspec || '',
      priorityix: this.mapLookupByCodeOrIx(
        priorities,
        item.priorityix,
        item.prioritycode
      ),
      kpinote: item.kpinote || '',
    }));

    const aiextract: IAiExtractForm = {
      promptix: draftResponse.meta?.promptix || selectedPromptix,
      rawinput: rawInput,
      structuredoutput: draftResponse,
      confidencevalue:
        draftResponse.meta?.confidencevalue === undefined
          ? ''
          : draftResponse.meta.confidencevalue,
      modelname: draftResponse.meta?.modelname || '',
    };

    this.setState({
      requisition,
      items,
      assumptions: draft.assumptions || [],
      clarifications: draft.clarifications || [],
      aiextract,
      success: 'AI draft generated successfully.',
    });
  };

  private handleGenerateDraft = async (): Promise<void> => {
    const { rawInput, requisition, selectedPromptix } = this.state;

    if (!rawInput.trim()) {
      this.setState({
        error: 'Please enter some raw request text before generating a draft.',
        success: '',
      });
      return;
    }

    this.setState({
      generating: true,
      error: '',
      success: '',
    });

    try {
      const response = await generateAiDraft({
        rawinput: rawInput,
        typeix: requisition.typeix || undefined,
        periodix: requisition.periodix || undefined,
        cycleix: requisition.cycleix || undefined,
        costcenterix: requisition.costcenterix || undefined,
        promptix: selectedPromptix || undefined,
      });

      this.mapAiDraftToForm(response);

      this.setState({
        generating: false,
      });
    } catch (error: any) {
      this.setState({
        generating: false,
        error: error?.message || 'Failed to generate AI draft',
      });
    }
  };

  private handleRefineDraft = async (): Promise<void> => {
    const { requisition, items, assumptions, clarifications, refineInstruction } = this.state;

    if (!refineInstruction.trim()) {
      this.setState({
        error: 'Please enter a refinement instruction.',
        success: '',
      });
      return;
    }

    this.setState({
      refining: true,
      error: '',
      success: '',
    });

    try {
      const response = await refineAiDraft({
        draft: {
          requisition,
          items,
          assumptions,
          clarifications,
        },
        instruction: refineInstruction,
      });

      this.mapAiDraftToForm(response);

      this.setState({
        refining: false,
      });
    } catch (error: any) {
      this.setState({
        refining: false,
        error: error?.message || 'Failed to refine AI draft',
      });
    }
  };

  private computeTotalEstimate = (): number => {
    return this.state.items.reduce((sum, item) => {
      const value =
        item.totalestimate === '' ? 0 : Number(item.totalestimate || 0);
      return sum + value;
    }, 0);
  };

  private openAddItemDialog = (): void => {
    this.setState((prevState) => ({
      itemDialogOpen: true,
      itemDialogMode: 'add',
      editingItemIndex: -1,
      editingItem: this.getEmptyItem(prevState.items.length + 1),
    }));
  };

  private openEditItemDialog = (index: number): void => {
    const item = this.state.items[index];

    this.setState({
      itemDialogOpen: true,
      itemDialogMode: 'edit',
      editingItemIndex: index,
      editingItem: { ...item },
    });
  };

  private closeItemDialog = (): void => {
    this.setState({
      itemDialogOpen: false,
      editingItemIndex: -1,
    });
  };

  private saveItemDialog = (value: IRequisitionItemForm): void => {
    const { itemDialogMode, editingItemIndex } = this.state;

    if (itemDialogMode === 'add') {
      this.setState((prevState) => ({
        itemDialogOpen: false,
        items: [...prevState.items, { ...value }],
      }));
      return;
    }

    if (itemDialogMode === 'edit' && editingItemIndex >= 0) {
      this.setState((prevState) => {
        const items = [...prevState.items];
        items[editingItemIndex] = { ...value };
        return {
          itemDialogOpen: false,
          items,
          editingItemIndex: -1,
        };
      });
    }
  };

  private handleDeleteItem = (index: number): void => {
    this.setState((prevState) => {
      const items = prevState.items
        .filter((_item, itemIndex) => itemIndex !== index)
        .map((item, itemIndex) => ({
          ...item,
          lineno: itemIndex + 1,
        }));

      return { items };
    });
  };

  private validateDraft = (): string[] => {
    const { requisition, items } = this.state;
    const errors: string[] = [];

    if (!requisition.recname.trim()) errors.push('Requisition title is required.');
    if (!requisition.description.trim()) errors.push('Description is required.');
    if (!requisition.typeix) errors.push('Requisition type is required.');
    if (!requisition.periodix) errors.push('Budget period is required.');
    if (!requisition.costcenterix) errors.push('Cost center is required.');
    if (!requisition.purpose.trim()) errors.push('Purpose is required.');
    if (!requisition.requesteddate) errors.push('Requested date is required.');
    if (items.length === 0) errors.push('At least one requisition item is required.');

    items.forEach((item, index) => {
      if (!item.recname.trim()) errors.push(`Item ${index + 1}: title is required.`);
      if (!item.description.trim()) errors.push(`Item ${index + 1}: description is required.`);
      if (!item.typeix) errors.push(`Item ${index + 1}: resource type is required.`);
      if (!item.justification.trim()) errors.push(`Item ${index + 1}: justification is required.`);
      if (item.totalestimate === '' || Number(item.totalestimate) < 0) {
        errors.push(`Item ${index + 1}: total estimate is required.`);
      }
    });

    return errors;
  };

  private handleSaveDraft = async (): Promise<void> => {
    const errors = this.validateDraft();

    if (errors.length > 0) {
      this.setState({
        error: errors.join(' '),
        success: '',
      });
      return;
    }

    this.setState({
      saving: true,
      error: '',
      success: '',
    });

    try {
      const payload: IRequisitionSavePayload = {
        requisition: {
          ...this.state.requisition,
          aiassisted: 1,
          rawinput: this.state.rawInput,
        },
        items: this.state.items.map((item, index) => ({
          ...item,
          lineno: index + 1,
        })),
        aiextract: this.state.aiextract,
      };

      const result = await createRequisition(payload);

      this.setState({
        saving: false,
        success: 'AI requisition draft saved successfully.',
      });

      const requisitionix = result?.requisitionix;
      if (requisitionix) {
        //const path = `/forms/requisitions/${requisitionix}/edit`;

        // if (this.props.navigate) {
        //   this.props.navigate(path);
        //   return;
        // }

        //window.location.href = path;

        if (this.props.navigate) {
          const row:any = {...payload, ...result};
          this.props.navigate('edit',row);
          return;
        }
      }
    } catch (error: any) {
      this.setState({
        saving: false,
        error: error?.message || 'Failed to save AI requisition draft',
      });
    }
  };

  private handleBack = (): void => {
    // if (this.props.navigate) {
    //   this.props.navigate('/forms/requisitions');
    //   return;
    // }

    //window.location.href = '/forms/requisitions';

    if (this.props.onClose) {
      this.props.onClose();
      return;
    }
  };

  render(): React.ReactNode {
    const {
      loading,
      generating,
      refining,
      saving,
      error,
      success,
      rawInput,
      refineInstruction,
      requisition,
      items,
      assumptions,
      clarifications,
      requisitionTypes,
      periods,
      cycles,
      costcenters,
      resourceTypes,
      uoms,
      priorities,
      prompts,
      selectedPromptix,
      itemDialogOpen,
      itemDialogMode,
      editingItem,
    } = this.state;

    const totalEstimate = this.computeTotalEstimate();

    return (
      <Box sx={{ p: 2 }}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          justifyContent="space-between"
          alignItems={{ xs: 'flex-start', md: 'center' }}
          spacing={2}
          sx={{ mb: 2 }}
        >
          <Box>
            <Typography variant="h5" fontWeight={700}>
              AI Requisition Draft
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Generate a structured requisition draft from natural language input.
            </Typography>
          </Box>

          <Stack direction="row" spacing={1}>
            <Button
              variant="outlined"
              startIcon={<ArrowBackIcon />}
              onClick={this.handleBack}
            >
              Back
            </Button>
            <Button
              variant="contained"
              startIcon={<SaveIcon />}
              onClick={this.handleSaveDraft}
              disabled={saving || generating || loading}
            >
              {saving ? 'Saving...' : 'Save Draft'}
            </Button>
          </Stack>
        </Stack>

        {error ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        ) : null}

        {success ? (
          <Alert severity="success" sx={{ mb: 2 }}>
            {success}
          </Alert>
        ) : null}

        <Grid container spacing={2}>
          <Grid size={{ xs: 12, md: 4 }}>
            <Card sx={{ mb: 2 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  AI Input
                </Typography>

                <Stack spacing={2}>
                  <TextField
                    fullWidth
                    label="Raw Request Input"
                    multiline
                    minRows={10}
                    value={rawInput}
                    onChange={this.handleRawInputChange}
                    placeholder="Describe the requisition in natural language..."
                  />

                  <FormControl fullWidth>
                    <InputLabel id="prompt-label">Prompt Template</InputLabel>
                    <Select
                      labelId="prompt-label"
                      value={selectedPromptix}
                      label="Prompt Template"
                      onChange={this.handlePromptChange}
                    >
                      <MenuItem value="">
                        <em>Default</em>
                      </MenuItem>
                      {prompts.map((item) => (
                        <MenuItem key={item.recix} value={item.recix}>
                          {item.recname}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  <FormControl fullWidth>
                    <InputLabel id="type-label">Requisition Type</InputLabel>
                    <Select
                      labelId="type-label"
                      value={requisition.typeix}
                      label="Requisition Type"
                      onChange={this.handleHeaderSelectChange('typeix')}
                    >
                      {requisitionTypes.map((item) => (
                        <MenuItem key={item.recix} value={item.recix}>
                          {item.recname}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  <FormControl fullWidth>
                    <InputLabel id="period-label">Budget Period</InputLabel>
                    <Select
                      labelId="period-label"
                      value={requisition.periodix}
                      label="Budget Period"
                      onChange={this.handleHeaderSelectChange('periodix')}
                    >
                      {periods.map((item) => (
                        <MenuItem key={item.recix} value={item.recix}>
                          {item.recname}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  <FormControl fullWidth>
                    <InputLabel id="cycle-label">Budget Cycle</InputLabel>
                    <Select
                      labelId="cycle-label"
                      value={requisition.cycleix}
                      label="Budget Cycle"
                      onChange={this.handleHeaderSelectChange('cycleix')}
                    >
                      <MenuItem value="">
                        <em>None</em>
                      </MenuItem>
                      {cycles.map((item) => (
                        <MenuItem key={item.recix} value={item.recix}>
                          {item.recname}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  <FormControl fullWidth>
                    <InputLabel id="costcenter-label">Cost Center</InputLabel>
                    <Select
                      labelId="costcenter-label"
                      value={requisition.costcenterix}
                      label="Cost Center"
                      onChange={this.handleHeaderSelectChange('costcenterix')}
                    >
                      {costcenters.map((item) => (
                        <MenuItem key={item.recix} value={item.recix}>
                          {item.recname}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  <Stack direction="row" spacing={1}>
                    <Button
                      fullWidth
                      variant="contained"
                      startIcon={<AutoAwesomeIcon />}
                      onClick={this.handleGenerateDraft}
                      disabled={generating || loading}
                    >
                      {generating ? 'Generating...' : 'Generate Draft'}
                    </Button>
                  </Stack>
                </Stack>
              </CardContent>
            </Card>

            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Refine Draft
                </Typography>

                <Stack spacing={2}>
                  <TextField
                    fullWidth
                    label="Refinement Instruction"
                    multiline
                    minRows={4}
                    value={refineInstruction}
                    onChange={this.handleRefineInstructionChange}
                    placeholder="E.g. split services from goods, improve justifications, make titles more concise..."
                  />

                  <Button
                    variant="outlined"
                    startIcon={<RefreshIcon />}
                    onClick={this.handleRefineDraft}
                    disabled={refining || generating || items.length === 0}
                  >
                    {refining ? 'Refining...' : 'Refine Draft'}
                  </Button>
                </Stack>
              </CardContent>
            </Card>
          </Grid>

          <Grid size={{ xs: 12, md: 8 }}>
            <Card sx={{ mb: 2 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Draft Header
                </Typography>

                <Grid container spacing={2}>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <TextField
                      fullWidth
                      label="Title"
                      value={requisition.recname}
                      onChange={this.handleHeaderTextChange('recname')}
                    />
                  </Grid>

                  <Grid size={{ xs: 12, md: 3 }}>
                    <TextField
                      fullWidth
                      label="Short Code"
                      value={requisition.shortcode}
                      onChange={this.handleHeaderTextChange('shortcode')}
                    />
                  </Grid>

                  <Grid size={{ xs: 12, md: 3 }}>
                    <TextField
                      fullWidth
                      label="Reference"
                      value={requisition.reference}
                      onChange={this.handleHeaderTextChange('reference')}
                    />
                  </Grid>

                  <Grid size={{ xs: 12 }}>
                    <TextField
                      fullWidth
                      label="Description"
                      multiline
                      minRows={2}
                      value={requisition.description}
                      onChange={this.handleHeaderTextChange('description')}
                    />
                  </Grid>

                  <Grid size={{ xs: 12 }}>
                    <TextField
                      fullWidth
                      label="Purpose"
                      multiline
                      minRows={2}
                      value={requisition.purpose}
                      onChange={this.handleHeaderTextChange('purpose')}
                    />
                  </Grid>

                  <Grid size={{ xs: 12, md: 4 }}>
                    <TextField
                      fullWidth
                      label="Requested Date"
                      type="date"
                      value={requisition.requesteddate}
                      onChange={this.handleHeaderTextChange('requesteddate')}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>

                  <Grid size={{ xs: 12, md: 4 }}>
                    <TextField
                      fullWidth
                      label="Needed From"
                      type="date"
                      value={requisition.neededfrom}
                      onChange={this.handleHeaderTextChange('neededfrom')}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>

                  <Grid size={{ xs: 12, md: 4 }}>
                    <TextField
                      fullWidth
                      label="Needed To"
                      type="date"
                      value={requisition.neededto}
                      onChange={this.handleHeaderTextChange('neededto')}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>

            <Card sx={{ mb: 2 }}>
              <CardContent>
                <Stack
                  direction={{ xs: 'column', md: 'row' }}
                  justifyContent="space-between"
                  alignItems={{ xs: 'flex-start', md: 'center' }}
                  spacing={2}
                  sx={{ mb: 2 }}
                >
                  <Typography variant="h6">Draft Items</Typography>

                  <Button
                    variant="contained"
                    startIcon={<PlaylistAddIcon />}
                    onClick={this.openAddItemDialog}
                  >
                    Add Item
                  </Button>
                </Stack>

                {items.length === 0 ? (
                  <Alert severity="info">No draft items generated yet.</Alert>
                ) : (
                  <Stack spacing={2}>
                    {items.map((item, index) => (
                      <Card key={`${index}-${item.recname}`} variant="outlined">
                        <CardContent>
                          <Stack
                            direction={{ xs: 'column', md: 'row' }}
                            justifyContent="space-between"
                            spacing={2}
                          >
                            <Box sx={{ flex: 1 }}>
                              <Typography variant="subtitle1" fontWeight={700}>
                                {item.lineno}. {item.recname || 'Untitled Item'}
                              </Typography>

                              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                {item.description || 'No description'}
                              </Typography>

                              <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mb: 1 }}>
                                {item.typeix ? <Chip size="small" label="Typed" /> : null}
                                {item.priorityix ? <Chip size="small" label="Priority Set" /> : null}
                                {item.kpinote ? <Chip size="small" label="KPI Noted" /> : null}
                              </Stack>

                              <Grid container spacing={1}>
                                <Grid size={{ xs: 12, md: 3 }}>
                                  <Typography variant="caption" color="text.secondary">
                                    Quantity
                                  </Typography>
                                  <Typography variant="body2">
                                    {item.quantity === '' ? '-' : item.quantity}
                                  </Typography>
                                </Grid>

                                <Grid size={{ xs: 12, md: 3 }}>
                                  <Typography variant="caption" color="text.secondary">
                                    Unit Estimate
                                  </Typography>
                                  <Typography variant="body2">
                                    {item.unitestimate === '' ? '-' : item.unitestimate}
                                  </Typography>
                                </Grid>

                                <Grid size={{ xs: 12, md: 3 }}>
                                  <Typography variant="caption" color="text.secondary">
                                    Total Estimate
                                  </Typography>
                                  <Typography variant="body2">
                                    {item.totalestimate === '' ? '-' : item.totalestimate}
                                  </Typography>
                                </Grid>

                                <Grid size={{ xs: 12, md: 3 }}>
                                  <Typography variant="caption" color="text.secondary">
                                    Justification
                                  </Typography>
                                  <Typography variant="body2">
                                    {item.justification || '-'}
                                  </Typography>
                                </Grid>
                              </Grid>
                            </Box>

                            <Stack direction="row" spacing={1}>
                              <Button
                                size="small"
                                startIcon={<EditIcon />}
                                onClick={() => this.openEditItemDialog(index)}
                              >
                                Edit
                              </Button>
                              <Button
                                size="small"
                                color="error"
                                startIcon={<DeleteOutlineIcon />}
                                onClick={() => this.handleDeleteItem(index)}
                              >
                                Delete
                              </Button>
                            </Stack>
                          </Stack>
                        </CardContent>
                      </Card>
                    ))}
                  </Stack>
                )}
              </CardContent>
            </Card>

            <Grid container spacing={2}>
              <Grid size={{ xs: 12, md: 6 }}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Assumptions
                    </Typography>
                    {assumptions.length === 0 ? (
                      <Typography variant="body2" color="text.secondary">
                        No assumptions.
                      </Typography>
                    ) : (
                      <Stack spacing={1}>
                        {assumptions.map((item, index) => (
                          <Alert key={index} severity="info">
                            {item}
                          </Alert>
                        ))}
                      </Stack>
                    )}
                  </CardContent>
                </Card>
              </Grid>

              <Grid size={{ xs: 12, md: 6 }}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Clarifications Needed
                    </Typography>
                    {clarifications.length === 0 ? (
                      <Typography variant="body2" color="text.secondary">
                        No clarifications.
                      </Typography>
                    ) : (
                      <Stack spacing={1}>
                        {clarifications.map((item, index) => (
                          <Alert key={index} severity="warning">
                            {item}
                          </Alert>
                        ))}
                      </Stack>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            </Grid>

            <Card sx={{ mt: 2 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Draft Summary
                </Typography>

                <Stack spacing={1}>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Item Count
                    </Typography>
                    <Typography variant="body1">{items.length}</Typography>
                  </Box>

                  <Divider />

                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Computed Total Estimate
                    </Typography>
                    <Typography variant="h6">
                      {Number(totalEstimate || 0).toLocaleString()}
                    </Typography>
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        <RequisitionItemDialog
          open={itemDialogOpen}
          title={itemDialogMode === 'add' ? 'Add Draft Item' : 'Edit Draft Item'}
          value={editingItem}
          resourceTypes={resourceTypes}
          uoms={uoms}
          priorities={priorities}
          onClose={this.closeItemDialog}
          onSave={this.saveItemDialog}
        />
      </Box>
    );
  }
}